/**
 * PR-A — Canonical Product Truth registry.
 *
 * Deterministic, read-only, machine-readable source of truth for Smart Shikka
 * product capabilities.  Each capability has exactly one canonical module owner,
 * one set of primary call-sites, and honest lifecycle stage flags.
 *
 * No execution, no network, no secrets.
 */
export const CANONICAL_PRODUCT_REGISTRY_VERSION = 'pra.v3';
export const CANONICAL_PRODUCT_REGISTRY_CHECKED_AT = '2026-07-19T22:31:21.000Z';
const PR_A_BASE_SHA = 'cd2b72caaea5ff827a9e5ec6fb2d82c8076a9b2f';
export function stage(implemented, wired, runtime_proven, production_proven, blocked) {
    return Object.freeze({
        implemented,
        wired,
        runtime_proven,
        production_proven,
        blocked,
    });
}
export function scope(owner, tenant, project) {
    return Object.freeze({ owner, tenant, project });
}
function ev(partial) {
    const checkedAt = CANONICAL_PRODUCT_REGISTRY_CHECKED_AT;
    const implemented = partial?.implemented
        ? { checkedSha: PR_A_BASE_SHA, checkedAt, ...partial.implemented }
        : undefined;
    const wired = partial?.wired
        ? { checkedSha: PR_A_BASE_SHA, checkedAt, ...partial.wired }
        : undefined;
    return Object.freeze({
        ...(implemented ? { implemented } : {}),
        ...(wired ? { wired } : {}),
        ...(partial?.runtime_proven
            ? { runtime_proven: { checkedSha: PR_A_BASE_SHA, checkedAt, ...partial.runtime_proven } }
            : {}),
        ...(partial?.production_proven
            ? {
                production_proven: { checkedSha: PR_A_BASE_SHA, checkedAt, ...partial.production_proven },
            }
            : {}),
    });
}
const ENTRIES = [
    {
        id: 'agent-orchestration',
        name: 'Smart Shikka Agent Orchestration',
        canonicalModule: 'packages/server/src/shikka-ceo',
        canonicalCallSites: [
            'packages/server/src/shikka-ceo',
            'packages/server/src/jarvis-router',
            'packages/server/src/runtime/runtimeComposition.ts',
            'packages/core/src/cwj-multi-agent-runtime',
            'packages/core/src/cwj-multi-agent-manager',
            'POST /api/ceo/turn',
            'POST /api/ceo/approve',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/AgentsLivePanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/shikka-ceo',
                description: 'Canonical CEO runtime, durable execution identity and planning-only Jarvis adapter are implemented under the server authority.',
            },
            wired: {
                testFile: 'packages/server/test/shikka-ceo/shikkaCeoService.test.ts',
                description: 'Canonical CEO turn and approval routes dispatch through the durable server orchestrator.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['cwj-multi-agent-runtime', 'multi-agent-manager', 'mission-runtime'],
        deprecated: false,
        category: 'orchestration',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'External provider idempotency guarantees and production execution evidence remain unverified.',
        docsLink: 'packages/server/src/shikka-ceo',
        summary: 'Canonical CEO planning, approval-gated execution, durable identity, verification and evidence authority.',
    },
    {
        id: 'tool-permission-execution',
        name: 'Smart Shikka Tool Permission / Execution',
        canonicalModule: 'packages/core/src/cwj-mcp-tool-router/toolRouter.ts',
        canonicalCallSites: [
            'packages/core/src/cwj-mcp-tool-router/toolRouter.ts',
            'packages/server/src/dispatchers/toolDispatch.ts',
            'POST /api/tools/execute',
            'POST /api/mcp/call',
            'GET /api/mcp-tools-permission/settings',
            'GET /api/execution/live/settings',
            'GET /execution-plans',
            'GET /adapters',
            'GET /policy-decisions',
            'POST /v1/mcp/read-only-tools/preview',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/ActionPermissionsPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/core/src/cwj-mcp-tool-router/toolRouter.ts',
                description: 'Canonical module packages/core/src/cwj-mcp-tool-router/toolRouter.ts and validated primary call-sites exist for tool-permission-execution.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['cwj-mcp-tool-router', 'mcp-tool-router', 'tool-router'],
        deprecated: false,
        category: 'tooling',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Fail-closed path traversal and command guards present; production sandbox and command allowlist review pending.',
        docsLink: 'packages/core/src/cwj-mcp-tool-router/README.md',
        summary: 'Fail-closed MCP/tool router with scoped permission, approval gating and workspace-root validation.',
    },
    {
        id: 'knowledge-bank',
        name: 'Smart Shikka Knowledge Bank',
        canonicalModule: 'packages/server/src/projectKnowledge/projectKnowledgeService.ts',
        canonicalCallSites: [
            'packages/server/src/projectKnowledge/projectKnowledgeService.ts',
            'packages/server/src/dispatchers/projectKnowledgeDispatch.ts',
            'packages/server/src/projectKnowledge/webIngestion.ts',
            'GET /api/knowledge/settings',
            'GET /api/knowledge/sources',
            'POST /api/knowledge/query',
            'GET /api/memory/settings',
            'GET /research-engine',
            'GET /v1/core/research-engine',
            'GET /knowledge',
            'GET /memory',
            'GET /v1/core/knowledge',
            'GET /v1/core/memory',
            'POST /v1/memory-retrieval/preview',
            'GET /api/knowledge-ingestion/settings',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/OwnerLibraryPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/projectKnowledge/projectKnowledgeService.ts',
                description: 'Canonical module packages/server/src/projectKnowledge/projectKnowledgeService.ts and validated primary call-sites exist for knowledge-bank.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['project-knowledge', 'knowledge-runtime', 'rag-store'],
        deprecated: false,
        category: 'knowledge',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Tenant/project isolation and restart persistence proven in tests; production vector backend optional.',
        docsLink: 'packages/server/src/projectKnowledge/README.md',
        summary: 'Tenant-scoped knowledge ingestion, citation, web ingestion guards and cross-project search.',
    },
    {
        id: 'code-intelligence',
        name: 'Smart Shikka Code Intelligence',
        canonicalModule: 'packages/server/src/codeIntelligence/index.ts',
        canonicalCallSites: [
            'packages/server/src/codeIntelligence/graphifyProvider.ts',
            'packages/server/src/codeIntelligence/codeIntelligenceService.ts',
            'packages/server/src/codeIntelligence/codeIntelligenceStore.ts',
            'packages/server/src/dispatchers/codeIntelligenceDispatch.ts',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/GitPilotPanel.tsx'],
        stageStatus: stage(true, false, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/codeIntelligence/index.ts',
                description: 'Canonical module packages/server/src/codeIntelligence/index.ts and validated primary call-sites exist for code-intelligence.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['graphify', 'code-graph', 'repo-indexer'],
        deprecated: false,
        category: 'code',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Code-intelligence runtime and dispatcher exist but are not exposed through any server route in the current manifest; true route wiring pending.',
        docsLink: 'packages/server/src/codeIntelligence/index.ts',
        summary: 'Graphify-backed code graph, search, trace and multi-process persistent indexer.',
    },
    {
        id: 'agent-skills',
        name: 'Smart Shikka Agent Skills',
        canonicalModule: 'packages/core/src/extension-platform/skillExecution.ts',
        canonicalCallSites: [
            'packages/core/src/extension-platform/skillExecution.ts',
            'packages/core/src/agent-skills',
            'packages/server/src/dispatchers/extensionPlatformDispatch.ts',
            'GET /api/skills/settings',
            'GET /api/skills/registry',
            'GET /browser-extension',
        ],
        uiSurfaces: ['apps/smart-shikka-browser-extension/src/sidepanel/WorkspacePanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/core/src/extension-platform/skillExecution.ts',
                description: 'Canonical module packages/core/src/extension-platform/skillExecution.ts and validated primary call-sites exist for agent-skills.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['extension-platform', 'skill-runtime', 'agent-skills'],
        deprecated: false,
        category: 'skills',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Real Linux bwrap sandbox and secure browser extension pairing present; production skill store signing pending.',
        docsLink: 'packages/core/src/extension-platform/README.md',
        summary: 'Sandboxed agent skill execution with safe browser extension pairing and OS-level isolation.',
    },
    {
        id: 'approval-governance',
        name: 'Smart Shikka Approval / Governance',
        canonicalModule: 'packages/core/src/production-governance',
        canonicalCallSites: [
            'packages/core/src/production-governance',
            'packages/server/src/dispatchers/controlDispatch.ts',
            'packages/server/src/dispatchers/moduleGroupApprovalHandlers.ts',
            'GET /api/consent-approval/settings',
            'GET /api/consent-approval/action-types',
            'GET /api/consent-runtime-bridge/settings',
            'GET /api/mcp-tools-permission/settings',
            'GET /api/security/hardening/status',
            'POST /v1/audit-store/preview',
            'POST /api/ceo/approve',
            'GET /approvals',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/JarvisControlPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/core/src/production-governance',
                description: 'Canonical module packages/core/src/production-governance and validated primary call-sites exist for approval-governance.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['production-governance', 'approval-engine', 'budget-gate'],
        deprecated: false,
        category: 'governance',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Cryptographically-bound lease approval and emergency stop wired; production HSM / key custody pending.',
        docsLink: 'packages/core/src/production-governance/README.md',
        summary: 'Approval tokens, lease-based execution gates, budget limits and emergency stop.',
    },
    {
        id: 'workspace-shell',
        name: 'Smart Shikka Workspace Shell',
        canonicalModule: 'packages/react/src/workspace',
        canonicalCallSites: [
            'packages/react/src/workspace/WorkspaceShell.tsx',
            'packages/react/src/workspace/useSharedWorkspaceShellProps.ts',
            'apps/smart-shikka-web/src/panels/WorkspacePanel.tsx',
            'apps/smart-shikka-browser-extension/src/sidepanel/WorkspacePanel.tsx',
            'apps/smart-shikka-desktop/src/renderer/shell',
            'GET /',
            'GET /apps',
            'GET /chat-panel',
            'GET /control-center',
            'GET /api/public/apps',
            'GET /api/projects',
            'GET /api/profiles/settings',
            'GET /api/session/settings',
            'GET /api/settings',
            'GET /api/settings/control-plane',
            'PATCH /api/settings/control-plane',
            'GET /api/settings-studio/status',
            'GET /studio',
        ],
        uiSurfaces: [
            'packages/react/src/workspace/WorkspaceShell.tsx',
            'apps/smart-shikka-web/src/panels/WorkspacePanel.tsx',
            'apps/smart-shikka-browser-extension/src/sidepanel/WorkspacePanel.tsx',
            'apps/smart-shikka-desktop/src/renderer/shell/DesktopWorkspacePanel.tsx',
        ],
        stageStatus: stage(true, true, true, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/react/src/workspace/useSharedWorkspaceShellProps.ts',
                checkedSha: 'a8b684f31608e04f2161bcea69b9c3e86617dff4',
                checkedAt: '2026-07-19T22:31:21.000Z',
                description: 'Shared useSharedWorkspaceShellProps hook centralizes WorkspaceShellProvider props (workspace, actor, chat, code, code-intelligence, cowork, memory, skills, project runtime) for web, browser extension, and desktop surfaces; owner/actor is derived from authenticated server role in CwjProjectRuntimeContext; isOwner=true is never hard-coded; command and patch mutations request checksum-bound single-use approval tokens from the server; remote-handoff is via capability policy, not surface label in the request.',
            },
            wired: {
                testFile: 'apps/smart-shikka-web/src/panels/WorkspacePanel.tsx',
                checkedSha: 'a8b684f31608e04f2161bcea69b9c3e86617dff4',
                checkedAt: '2026-07-19T22:31:21.000Z',
                description: 'Web WorkspacePanel, browser-extension WorkspacePanel, and DesktopWorkspacePanel all consume useSharedWorkspaceShellProps and render WorkspaceShell through WorkspaceShellProvider; workspaceCodeService implements requestPatchApproval, approvePatch (apply/publish/rollback tokens), applyPatch, publishPatch, rollback, requestCommandApproval, and runCommand so the full proposal → approval → token → apply/publish → rollback flow has a real method/call-site; extension tsconfig uses the published React workspace package; desktop surface now receives a trusted bearer session from Electron main/preload via getWorkspaceSession() and creates a real ProjectRuntimeServiceClient, falling back to BLOCKED_WITH_REASON when no active desktop session or server config is available.',
            },
            runtime_proven: {
                testFile: 'packages/react/test/workspaceServices.test.ts',
                checkedSha: 'a8b684f31608e04f2161bcea69b9c3e86617dff4',
                checkedAt: '2026-07-19T22:31:21.000Z',
                description: 'workspaceServices.test.ts proves full patch flow with checksum-bound tokens and command approval token issuance; desktopWorkspaceShell.test.ts proves DesktopWorkspacePanel uses getWorkspaceSession, createDesktopProjectRuntimeServiceClient, and shared useSharedWorkspaceShellProps/WorkspaceShell and does not hardcode owner state; workspacePanelSecurity.test.ts proves the extension panel derives ownership from the shared shell and does not hardcode isOwner:true; workspaceSession.test.ts proves the main-process resolver is fail-closed for missing session, missing server config, and invalid URLs; production desktop build and extension build/package smoke pass. Real end-to-end browser/Electron/extension journeys require a configured server token + running server, so those remain BLOCKED_WITH_REASON.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['unified-workspace', 'desktop-shell', 'web-shell'],
        deprecated: false,
        category: 'surface',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Web and browser-extension now share the WorkspaceShell view-model via useSharedWorkspaceShellProps; DesktopShell routes the workspace surface to DesktopWorkspacePanel using the same shared hook and WorkspaceShellProvider. Server-backed code/chat execution on the desktop is BLOCKED_WITH_REASON (no bearer-token bridge from main process); real browser/Electron/extension journeys and project-switch isolation pending owner integration run with the token bridge.',
        docsLink: 'packages/react/src/workspace/README.md',
        summary: 'Shared WorkspaceShell / UnifiedWorkspace UI surface for chat, code, cowork, knowledge and approvals.',
    },
    {
        id: 'streaming-conversation',
        name: 'Smart Shikka Streaming Conversation',
        canonicalModule: 'packages/server/src/chat',
        canonicalCallSites: [
            'packages/server/src/chat/chatService.ts',
            'packages/server/src/dispatchers/chatDispatch.ts',
            'POST /api/ask',
            'POST /api/public/ask',
            'GET /api/chat/page/settings',
            'POST /api/chat/page/session/preview',
            'GET /api/server-side-llm-gate/settings',
            'GET /api/provider-router/settings',
            'POST /api/provider-router/route-preview',
            'GET /api/provider-privacy/providers',
            'GET /api/vector-embedding/providers',
            'GET /v1/models',
            'POST /v1/chat/completions',
            'POST /v1/provider-registry/preview',
            'GET /api/staging-provider-call/settings',
            'GET /v1/gateway/observability/preview-status',
        ],
        uiSurfaces: [
            'apps/smart-shikka-desktop/src/renderer/shell/ShikkaChatPanel.tsx',
            'apps/smart-shikka-desktop/src/renderer/shell/LlmChatPilotPanel.tsx',
        ],
        stageStatus: stage(true, true, true, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/chat/chatService.ts',
                checkedSha: '7673220e8c36f0211f8c798ec3d3e5a03f01d1f3',
                checkedAt: '2026-07-14T13:42:20.000Z',
                description: 'ChatService implemented with owner/tenant/project scoped Postgres conversation/key stores, encrypted provider keys, server-side governance budget, early releaseBudgetReservation guard for every post-reserve path, per-chunk emergency stop abort, and SSE dispatch.',
            },
            wired: {
                testFile: 'packages/server/src/runtime/runtimeComposition.ts',
                checkedSha: '7673220e8c36f0211f8c798ec3d3e5a03f01d1f3',
                checkedAt: '2026-07-14T13:42:20.000Z',
                description: 'Singleton ChatService injected into canonical server runtime composition; authenticated /api/chat/* routes fail-closed (503) when the runtime is unavailable.',
            },
            runtime_proven: {
                testFile: 'packages/server/test/chat',
                checkedSha: '7673220e8c36f0211f8c798ec3d3e5a03f01d1f3',
                checkedAt: '2026-07-14T13:42:20.000Z',
                description: 'chat.test.ts proves 100+ turn Postgres restart recovery, store write failure budget release, and cross-tenant/project access denial; chatDispatch.test.ts proves authenticated route dispatch; chatRealProviderSmoke.test.ts proves local HTTP SSE adapter smoke; chatBudgetConcurrency.real.test.ts proves real PostgreSQL 25-reservation serialization and cross-tenant/project isolation; chatBudgetConcurrency.test.ts is a pg-mem unit test. Production provider keys are not included, so live provider runtime proof remains pending.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['chat-runtime', 'conversation-runtime', 'llm-chat'],
        deprecated: false,
        category: 'conversation',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: true,
        ownerActionNeeded: 'PR-B wires a real authenticated call-chain (UI/IPC → /api/chat → resolveActor → project access → ChatService → Postgres store/provider adapter) and adds server-side budget/abort governance. The llm-runtime router.ts retry/fallback logic is pre-existing; this PR only registers the llamacpp adapter. Production provider keys are required for live provider runtime proof.',
        docsLink: 'packages/server/src/chat/README.md',
        summary: 'Streaming conversation runtime with durable history, compaction, citations and tool-call handoff.',
    },
    {
        id: 'local-coding-loop',
        name: 'Smart Shikka Local Coding Loop',
        canonicalModule: 'packages/server/src/codeControl',
        canonicalCallSites: [
            'packages/server/src/codeControl/codeControlService.ts',
            'packages/server/src/dispatchers/projectKnowledgeDispatch.ts',
        ],
        uiSurfaces: [
            'apps/smart-shikka-desktop/src/renderer/shell/GitPilotPanel.tsx',
        ],
        stageStatus: stage(true, true, true, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/codeControl/codeControlService.ts',
                checkedSha: '3cf18138406e7eb679d161165a1d8701ace04887',
                checkedAt: '2026-07-19T19:37:19.000Z',
                description: 'CodeControlService uses Postgres-backed CodeControlLeaseStore scoped by (ownerId, tenantId, projectId, leaseId) with atomic claim and single-owner updates; creates isolated Git worktree leases via GitWorktreeSandbox; applies/rolls back patches inside the worktree through SmartShikkaPatchApplyEngine with storeDir pointing to the main repo .smart-shikka; consumes owner/tenant/project/action/checksum-bound single-use approval tokens for apply/publish/rollback and for mutating/network-capable commands; enforces default-deny env allowlist, child-process-group cancellation, and orphan cleanup; validates realpath confinement of project root, patch target, and recovered lease path; adds publish semantics that atomically commit the worktree diff to main after base-SHA verification; and approvePatch now issues distinct apply/publish/rollback tokens so the full UI patch flow can bind each step to a single-use token.',
            },
            wired: {
                testFile: 'packages/server/src/dispatchers/projectKnowledgeDispatch.ts',
                checkedSha: '3cf18138406e7eb679d161165a1d8701ace04887',
                checkedAt: '2026-07-19T19:37:19.000Z',
                description: 'ProjectKnowledgeDispatch constructs ApprovalTokenStore from options.approvalTokenBackend + SMART_SHIKKA_APPROVAL_TOKEN_SECRET and fails closed with 503 when the backend/secret is missing in production; injects GitWorktreeSandbox and the durable Postgres lease store into CodeControlService; approves patches from authenticated server actor/role only; and exposes POST /api/projects/:projectId/patches/:proposalId/{approve,apply,publish,rollback} and POST /api/projects/:projectId/commands/approve and /commands. routeDispatch.ts passes dbClient and approvalTokenBackend.',
            },
            runtime_proven: {
                testFile: 'packages/server/test/codeControl/codeControlRealWorktree.test.ts',
                checkedSha: '3cf18138406e7eb679d161165a1d8701ace04887',
                checkedAt: '2026-07-19T19:37:19.000Z',
                description: 'codeControlRealWorktree.test.ts proves proposal → approve → apply in worktree leaves main repo unchanged, rollback restores and cleans lease, and token/mismatched approval rejection. codeControlDispatch.test.ts proves authenticated route dispatch wiring, allowlisted command with env redaction, and disallowed command/shell-metachar rejection. codeControlPostgres.test.ts proves durable Postgres lease persistence across two service instances (restart recovery), concurrent same-lease claim, forged approval token rejection, symlink worktree escape rejection, timeout/cancel with orphan cleanup, and atomic publish/rollback to exact base SHA.',
            },
        }),
        scope: scope(true, false, true),
        aliases: ['code-control', 'file-editor', 'claude-code-loop'],
        deprecated: false,
        category: 'code',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Isolated Git worktree sandbox, approval-bound patch/rollback and command sandbox are implemented and runtime-proven in unit tests; production proof on real project repositories pending owner integration run.',
        docsLink: 'packages/server/src/codeControl/README.md',
        summary: 'Claude Code-style workspace loop: read, plan, approve, edit, test, diff, checkpoint, commit.',
    },
    {
        id: 'browser-control',
        name: 'Smart Shikka Browser Controller',
        canonicalModule: 'packages/server/src/browser-controller/browserControllerClient.ts',
        canonicalCallSites: [
            'packages/server/src/browser-controller/browserControllerClient.ts',
            'packages/server/src/dispatchers/browserDesktopDispatch.ts',
            'GET /api/browser/connector/settings',
            'GET /api/browser-desktop-hardening/settings',
            'GET /api/browser-desktop-hardening/overview-cards',
            'GET /browser-extension',
            'GET /v1/core/browser-extension',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/BrowserControllerPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/browser-controller/browserControllerClient.ts',
                description: 'Canonical module packages/server/src/browser-controller/browserControllerClient.ts and validated primary call-sites exist for browser-control.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, false, false),
        aliases: ['browser-controller', 'browser-desktop', 'browser-read'],
        deprecated: false,
        category: 'browser',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Browser read/observe routes present; assisted actions require approval and dangerous actions are permanently blocked.',
        docsLink: 'packages/server/src/browser-controller/README.md',
        summary: 'Safe browser read/observe/assisted actions with dangerous action blocklist.',
    },
    {
        id: 'voice-multimodal',
        name: 'Smart Shikka Voice / Multimodal',
        canonicalModule: 'apps/smart-shikka-desktop/src/main/voicePilotExecutor.ts',
        canonicalCallSites: [
            'apps/smart-shikka-desktop/src/main/voicePilotExecutor.ts',
            'apps/smart-shikka-desktop/src/main/voiceEngineResolver.ts',
            'apps/smart-shikka-desktop/src/main/voicePilotIpcHandlers.ts',
            'apps/smart-shikka-desktop/src/renderer/VoiceEnginePanel.tsx',
            'packages/react/src/components/voice/VoiceAgent.tsx',
            'packages/server/src/jarvis-router/jarvisRouter.ts',
        ],
        uiSurfaces: [
            'packages/react/src/components/voice/VoiceAgent.tsx',
            'apps/smart-shikka-desktop/src/renderer/VoiceEnginePanel.tsx',
        ],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'apps/smart-shikka-desktop/test/voiceEngineResolver.test.ts',
                description: 'Real STT (whisper.cpp) and TTS (espeak-ng/Indic Parler sidecar) executors in desktop main process.',
            },
            wired: {
                testFile: 'packages/server/src/jarvis-router/jarvisRouter.ts',
                description: 'CEO/Jarvis tool route voice.preview delegates to real voice pilot.',
            },
            runtime_proven: {
                testFile: 'apps/smart-shikka-desktop/test/voiceEngineResolver.test.ts',
                description: 'Desktop voice engine resolver routes STT/TTS through real sidecars and mock fallback is blocked.',
            },
        }),
        scope: scope(true, true, false),
        aliases: ['voice', 'voice-agent', 'multimodal'],
        deprecated: false,
        category: 'voice',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Requires local whisper.cpp model and espeak-ng binary, or owner-configured Indic Parler TTS sidecar on loopback.',
        docsLink: 'docs/testing/VOICE_WINDOWS_RUNTIME_CHECKLIST.md',
        summary: 'Real push-to-talk STT/TTS in desktop; no silent mock fallback.',
    },
    {
        id: 'design-studio',
        name: 'Smart Shikka Design Studio',
        canonicalModule: 'packages/server/src/design/designProjectService.ts',
        canonicalCallSites: [
            'packages/server/src/design/designProjectService.ts',
            'packages/server/src/dispatchers/designStudioDispatch.ts',
            'GET /studio',
            'GET /studio/app-studio',
            'GET /studio/app-detail',
            'GET /v1/core/hindi-jarvis-studio-ui',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/DesignView.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/design/designProjectService.ts',
                description: 'Canonical module packages/server/src/design/designProjectService.ts and validated primary call-sites exist for design-studio.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['design-studio', 'app-studio', 'jarvis-studio'],
        deprecated: false,
        category: 'design',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Design project service in-memory preview only; durable tenant-scoped store and asset validation pending.',
        docsLink: 'packages/server/src/design/README.md',
        summary: 'UI/app design project preview with asset and layout metadata.',
    },
    {
        id: 'mobile-pairing',
        name: 'Smart Shikka Mobile Pairing',
        canonicalModule: 'packages/server/src/mobilePairing/mobilePairingService.ts',
        canonicalCallSites: [
            'packages/server/src/mobilePairing/mobilePairingService.ts',
            'packages/server/src/mobilePairing/mobilePairingRuntime.ts',
            'apps/smart-shikka-desktop/src/renderer/shell/MobileActionSyncPanel.tsx',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/MobileActionSyncPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/mobilePairing/mobilePairingService.ts',
                description: 'Canonical module packages/server/src/mobilePairing/mobilePairingService.ts and validated primary call-sites exist for mobile-pairing.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['mobile-pairing', 'mobile-sync', 'mobile-action'],
        deprecated: false,
        category: 'mobile',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Mobile pairing runtime exists; route exposure and cross-device security audit pending.',
        docsLink: 'packages/server/src/mobilePairing/README.md',
        summary: 'Mobile companion pairing for approvals and safe action handoff.',
    },
    {
        id: 'artifact-cowork',
        name: 'Smart Shikka Artifact / Cowork',
        canonicalModule: 'packages/server/src/dispatchers/coworkDispatch.ts',
        canonicalCallSites: [
            'packages/server/src/dispatchers/coworkDispatch.ts',
            'GET /api/chat-workspace/settings',
            'POST /api/chat-workspace/session/preview',
            'POST /api/chat-workspace/message/preview',
            'POST /api/chat-workspace/workflow-draft/preview',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/DesktopWorkspacePanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/dispatchers/coworkDispatch.ts',
                description: 'Canonical module packages/server/src/dispatchers/coworkDispatch.ts and validated primary call-sites exist for artifact-cowork.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['cowork', 'artifact', 'chat-workspace'],
        deprecated: false,
        category: 'orchestration',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Cowork artifact workspace routes wired; durable runtime proof and conflict resolution pending.',
        docsLink: 'packages/server/src/dispatchers/coworkDispatch.ts',
        summary: 'Shared workspace artifacts, chat workspace sessions and cowork memory.',
    },
    {
        id: 'external-app-adapters',
        name: 'Smart Shikka External App Adapters',
        canonicalModule: 'packages/server/src/provider/providerRegistry.ts',
        canonicalCallSites: [
            'packages/server/src/provider/providerRegistry.ts',
            'GET /api/connectors/dify/settings',
            'POST /api/connectors/dify/dry-run',
            'GET /api/connectors/hermes/settings',
            'GET /v1/gateway/observability/preview-status',
            'GET /v1/gateway/observability/recent-events',
            'POST /v1/gateway/preview',
            'POST /api/workflows/n8n',
            'GET /goose',
            'GET /v1/core/goose',
            'GET /external-services',
            'GET /v1/core/external-services',
            'packages/core/src/integrations/sevenAppCapabilityManifest.ts',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/LiveConnectorPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/provider/providerRegistry.ts',
                description: 'Canonical module packages/server/src/provider/providerRegistry.ts and validated primary call-sites exist for external-app-adapters.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['external-adapters', 'connectors', 'goose-bridge'],
        deprecated: false,
        category: 'external',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Connector preview routes wired; live external API calls remain dry-run and approval-gated.',
        docsLink: 'packages/server/src/provider/README.md',
        summary: 'Dry-run adapters for external agents, services, gateways and workflows.',
    },
    {
        id: 'operations-health',
        name: 'Smart Shikka Operations / Health',
        canonicalModule: 'packages/server/src/routes/healthRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/healthRoutes.ts',
            'packages/server/src/dispatchers/policyDispatch.ts',
            'GET /health',
            'POST /api/validate-settings',
            'GET /api/first-real-healthcheck/settings',
            'GET /api/operator-telemetry-status/settings',
            'GET /api/server-side-healthcheck-adapter/settings',
            'GET /api/real-runtime-settings/settings',
            'GET /api/local-staging-data-adapter/settings',
            'GET /api/read-only-data-adapter/settings',
            'GET /api/reference-settings-reconciliation/settings',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/StatusPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/healthRoutes.ts',
                description: 'Canonical module packages/server/src/routes/healthRoutes.ts and validated primary call-sites exist for operations-health.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, false),
        aliases: ['operations', 'health', 'telemetry'],
        deprecated: false,
        category: 'health',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Health and telemetry preview routes present; production observability stack and real DB probes pending.',
        docsLink: 'packages/server/src/routes/healthRoutes.ts',
        summary: 'Health checks, telemetry, runtime settings and data-adapter previews.',
    },
    {
        id: 'distribution',
        name: 'Smart Shikka Distribution',
        canonicalModule: 'packages/server/src/dispatchers/moduleGroupDeploymentHandlers.ts',
        canonicalCallSites: [
            'packages/server/src/dispatchers/moduleGroupDeploymentHandlers.ts',
            '/api/beta-readiness',
            '/api/beta-release-audit-pack',
            '/api/preview-beta-rc',
            '/api/deployment-validation',
            '/api/docker-compose-deployment-runbook',
            '/api/vps-deployment-topology',
            '/api/vps-stability-validation',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/DeployPilotPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/dispatchers/moduleGroupDeploymentHandlers.ts',
                description: 'Canonical module packages/server/src/dispatchers/moduleGroupDeploymentHandlers.ts and validated primary call-sites exist for distribution.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, false, false),
        aliases: ['distribution', 'deployment', 'release-pack'],
        deprecated: false,
        category: 'distribution',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Deployment packaging preview handlers wired to manifest; production rollout execution and real infrastructure mutation are out of PR-A scope.',
        docsLink: 'packages/server/src/dispatchers/moduleGroupDeploymentHandlers.ts',
        summary: 'Deployment packaging, release candidate, VPS/Docker topology and beta-readiness preview handlers.',
    },
    {
        id: 'messaging',
        name: 'Smart Shikka Messaging',
        canonicalModule: 'packages/server/src/routes/coreRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/coreRoutes.ts',
            'GET /messaging',
            'GET /v1/core/messaging',
            'POST /v1/core/messaging/preview',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/shell/MessagingPilotPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/coreRoutes.ts',
                description: 'Canonical module packages/server/src/routes/coreRoutes.ts and validated primary call-sites exist for messaging.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, false),
        aliases: ['messaging', 'whatsapp-telegram', 'chat-channels'],
        deprecated: false,
        category: 'messaging',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: true,
        ownerActionNeeded: 'Messaging connector preview routes present; no real WhatsApp/Telegram API calls until owner approval and secrets configured.',
        docsLink: 'packages/server/src/routes/coreRoutes.ts',
        summary: 'WhatsApp / Telegram / messaging connector preview and policy gates.',
    },
    {
        id: 'provider-privacy',
        name: 'Smart Shikka Provider Privacy',
        canonicalModule: 'packages/server/src/routes/providerRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/providerRoutes.ts',
            'GET /api/provider-privacy/settings',
            'POST /api/provider-privacy/classify-preview',
            'POST /api/provider-privacy/redact-preview',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/StatusPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/providerRoutes.ts',
                description: 'Canonical module packages/server/src/routes/providerRoutes.ts and validated primary call-sites exist for provider-privacy.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['provider-privacy', 'outbound-privacy', 'data-class-guard'],
        deprecated: false,
        category: 'governance',
        status: 'preview',
        defaultEnabled: true,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Provider privacy classification/redaction preview wired; live outbound policy enforcement pending.',
        docsLink: 'packages/server/src/routes/providerRoutes.ts',
        summary: 'Outbound payload data-class detection, redaction and routing policy preview.',
    },
    {
        id: 'vector-embedding',
        name: 'Smart Shikka Vector Embedding',
        canonicalModule: 'packages/server/src/routes/providerRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/providerRoutes.ts',
            'GET /api/vector-embedding/settings',
            'POST /api/vector-embedding/adapter-preview',
            'POST /api/vector-embedding/provider-preview',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/StatusPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/providerRoutes.ts',
                description: 'Canonical module packages/server/src/routes/providerRoutes.ts and validated primary call-sites exist for vector-embedding.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['vector-embedding', 'embedding-adapter'],
        deprecated: false,
        category: 'knowledge',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: false,
        requiresSecrets: true,
        ownerActionNeeded: 'Vector/embedding adapter preview routes present; live embedding generation gated by provider secret.',
        docsLink: 'packages/server/src/routes/providerRoutes.ts',
        summary: 'Vector embedding adapter and provider preview with disabled writes.',
    },
    {
        id: 'drishti-kundali',
        name: 'Smart Shikka Drishti Kundali',
        canonicalModule: 'packages/server/src/routes/miscRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/miscRoutes.ts',
            'GET /api/drishti-kundali/settings',
            'GET /api/drishti-kundali/mcp/tools',
            'POST /api/drishti-kundali/input/validate-preview',
            'GET /api/drishti-service-adapter/settings',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/StatusPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/miscRoutes.ts',
                description: 'Canonical module packages/server/src/routes/miscRoutes.ts and validated primary call-sites exist for drishti-kundali.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, false),
        aliases: ['drishti', 'drishti-service-adapter'],
        deprecated: false,
        category: 'external',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: false,
        ownerActionNeeded: 'Drishti Kundali preview routes present; deterministic calculation engine and external call policy pending.',
        docsLink: 'packages/server/src/routes/miscRoutes.ts',
        summary: 'Deterministic astrology/Kundali service adapter preview.',
    },
    {
        id: 'provider-credential-vault',
        name: 'Smart Shikka Provider Credential Vault',
        canonicalModule: 'packages/server/src/routes/providerRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/providerRoutes.ts',
            'GET /api/provider-credential-vault/settings',
            'POST /api/provider-credential-vault/state-preview',
            'GET /api/provider-credential-vault/boundary-matrix',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/StatusPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/providerRoutes.ts',
                description: 'Canonical module packages/server/src/routes/providerRoutes.ts and validated primary call-sites exist for provider-credential-vault.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['credential-vault', 'secret-boundary', 'provider-vault'],
        deprecated: false,
        category: 'governance',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: true,
        requiresSecrets: true,
        ownerActionNeeded: 'Credential vault preview routes present; real vault backend and HSM integration pending.',
        docsLink: 'packages/server/src/routes/providerRoutes.ts',
        summary: 'Provider key lifecycle, secret boundary matrix and copy-guard preview.',
    },
    {
        id: 'token-economy-agent-router',
        name: 'Smart Shikka Token Economy Agent Router',
        canonicalModule: 'packages/server/src/routes/providerRoutes.ts',
        canonicalCallSites: [
            'packages/server/src/routes/providerRoutes.ts',
            'GET /api/token-economy-agent-router/settings',
            'POST /api/token-economy-agent-router/state-preview',
            'GET /api/token-economy-agent-router/provider-budget',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/StatusPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes/providerRoutes.ts',
                description: 'Canonical module packages/server/src/routes/providerRoutes.ts and validated primary call-sites exist for token-economy-agent-router.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['token-economy', 'budget-router', 'token-router'],
        deprecated: false,
        category: 'governance',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Token economy preview routes present; runtime budget enforcement and provider billing pending.',
        docsLink: 'packages/server/src/routes/providerRoutes.ts',
        summary: 'Provider budget, token quota and abuse-policy preview router.',
    },
    {
        id: 'platform-internal',
        name: 'Smart Shikka Platform Internal / Audit Preview',
        canonicalModule: 'packages/server/src/routes',
        canonicalCallSites: [
            'packages/server/src/serverRoutes.ts',
            'packages/server/src/routes/routeCanonicalOwners.ts',
        ],
        uiSurfaces: ['apps/smart-shikka-desktop/src/renderer/FeatureInventoryPanel.tsx'],
        stageStatus: stage(true, true, false, false, false),
        stageEvidence: ev({
            implemented: {
                testFile: 'packages/server/src/routes',
                description: 'Canonical module packages/server/src/routes and validated primary call-sites exist for platform-internal.',
            },
            wired: {
                testFile: 'packages/server/test/routeCanonicalOwners.test.ts',
                description: 'Route ownership and wiring validated against the full server route manifest.',
            },
        }),
        scope: scope(true, true, true),
        aliases: ['preview-foundation', 'internal-surface', 'audit-preview'],
        deprecated: false,
        category: 'platform',
        status: 'preview',
        defaultEnabled: false,
        requiresApproval: false,
        requiresSecrets: false,
        ownerActionNeeded: 'Explicit owner for platform-internal preview/audit/sandbox routes listed in routeCanonicalOwners; no catch-all fallback remains.',
        docsLink: 'packages/server/src/routes/routeCanonicalOwners.ts',
        summary: 'Explicit canonical owner for platform-internal preview, audit and dry-run surfaces.',
    },
];
export function buildCanonicalProductRegistry() {
    return Object.freeze({
        version: CANONICAL_PRODUCT_REGISTRY_VERSION,
        checkedAt: CANONICAL_PRODUCT_REGISTRY_CHECKED_AT,
        entries: Object.freeze(ENTRIES.map((e) => Object.freeze({ ...e }))),
    });
}
export function listCanonicalProductEntries() {
    return buildCanonicalProductRegistry().entries;
}
export function findCanonicalProductEntry(idOrAlias) {
    const normalized = idOrAlias.toLowerCase().trim();
    for (const e of ENTRIES) {
        if (e.id === normalized)
            return e;
        if (e.aliases.some((a) => a.toLowerCase() === normalized))
            return e;
    }
    return undefined;
}
export function resolveCanonicalAlias(idOrAlias) {
    const e = findCanonicalProductEntry(idOrAlias);
    if (!e)
        return undefined;
    return {
        canonicalId: e.id,
        deprecated: e.deprecated,
        replacementId: e.deprecationReplacementId,
    };
}
export function validateCanonicalProductRegistry() {
    const errors = [];
    const ids = new Set();
    const aliases = new Map();
    for (const e of ENTRIES) {
        if (ids.has(e.id)) {
            errors.push(`Duplicate canonical id: ${e.id}`);
        }
        ids.add(e.id);
        if (e.deprecated && !e.deprecationReplacementId) {
            errors.push(`Deprecated entry '${e.id}' is missing deprecationReplacementId`);
        }
        if (e.deprecated && e.deprecationReplacementId === e.id) {
            errors.push(`Deprecated entry '${e.id}' cannot replace itself`);
        }
        for (const a of e.aliases) {
            const existing = aliases.get(a);
            if (existing && existing !== e.id) {
                errors.push(`Alias '${a}' is used by both '${existing}' and '${e.id}'`);
            }
            aliases.set(a, e.id);
        }
        if (e.stageStatus.blocked && e.status !== 'blocked') {
            errors.push(`Blocked stage flag for '${e.id}' must match status 'blocked'`);
        }
        if (e.status === 'blocked' && !e.stageStatus.blocked) {
            errors.push(`Status 'blocked' for '${e.id}' requires blocked stage flag true`);
        }
        if (e.status === 'missing' && e.stageStatus.implemented) {
            errors.push(`Missing status for '${e.id}' cannot have implemented=true`);
        }
        if (e.stageStatus.production_proven && !e.stageStatus.runtime_proven) {
            errors.push(`Production proven for '${e.id}' requires runtime_proven=true`);
        }
        if (e.stageStatus.runtime_proven && !e.stageStatus.wired) {
            errors.push(`Runtime proven for '${e.id}' requires wired=true`);
        }
        if (e.stageStatus.wired && !e.stageStatus.implemented) {
            errors.push(`Wired for '${e.id}' requires implemented=true`);
        }
        for (const stage of ['runtime_proven', 'production_proven']) {
            if (e.stageStatus[stage]) {
                const evidence = e.stageEvidence[stage];
                if (!evidence || !evidence.checkedSha || !evidence.checkedAt) {
                    errors.push(`Stage '${stage}' for '${e.id}' requires evidence with checkedSha and checkedAt`);
                }
            }
        }
    }
    return Object.freeze({ valid: errors.length === 0, errors: Object.freeze(errors) });
}
export function findDeprecatedCanonicalEntries() {
    return ENTRIES.filter((e) => e.deprecated);
}
export function groupCanonicalEntriesByCategory(entries = listCanonicalProductEntries()) {
    var _a;
    const groups = {};
    for (const e of entries) {
        (groups[_a = e.category] ?? (groups[_a] = [])).push(e);
    }
    return Object.freeze(groups);
}
//# sourceMappingURL=canonicalProductRegistry.js.map